import React from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Issue } from 'api/api.types';
import ProgressBar from 'lib/ProgressBar/ProgressBar';
import { getInternalUrl } from 'utils/routing';
import getIssueType, { ISSUE_FILTER_TYPES } from 'utils/getIssueType';
import ElectionTeaserResult from './ElectionTeaserResult';
import ElectionTeaserInfo from './ElectionTeaserInfo';
import useElectionTeaserTitle from './useElectionTeaserTitle';

const PROPORTIONAL_ELECTIONS: string[] = [
  ISSUE_FILTER_TYPES.grossRat,
  ISSUE_FILTER_TYPES.nationalCouncil,
];

interface Props {
  election: Issue;
  isFinal: boolean;
  headingLevel?: 3 | 4;
}

const ElectionTeaser = ({ election, isFinal, headingLevel }: Props) => {
  const { t } = useTranslation('wap');
  const title = useElectionTeaserTitle(election?.info);

  const electionInfo =
    election.info.majority_election_info ?? election.info.proportional_election_info;

  const roundNumber = 'round_number' in electionInfo && electionInfo?.round_number;
  const totalSeats = electionInfo?.seat_count ?? 0;
  const claimedSeats =
    election.lists?.reduce((sum, val) => sum + (val.results?.seat_claim ?? 0), 0) ??
    election.candidates?.reduce((sum, val) => sum + (val.results?.elected ? 1 : 0), 0);
  const nextElectionDate = election.results?.majority_election_data?.second_round_date;

  const notAllSeatsFilled = claimedSeats < totalSeats && claimedSeats > 0;
  const noSeats = claimedSeats === 0;
  const showInfo = isFinal && (notAllSeatsFilled || noSeats);

  const HeadingTag = `h${headingLevel}` as 'h3' | 'h4';

  const isProportional = PROPORTIONAL_ELECTIONS.includes(getIssueType(election.info));

  return (
    <div className="electionteaser__outer">
      <ProgressBar issue={election} hideCountedCircles />
      <div className="electionteaser">
        <Link to={getInternalUrl(election.info, 'poll')}>
          <div className="electionteaser__inner">
            <div
              className={classNames('electionteaser__content', {
                'electionteaser__content--final': isFinal,
              })}
            >
              <div className="electionteaser__top">
                <HeadingTag className="electionteaser__heading">
                  {/* eslint-disable-next-line react/no-danger */}
                  <span dangerouslySetInnerHTML={{ __html: title }} />
                </HeadingTag>
                <div className="electionteaser__meta">
                  <div className="electionteaser__meta-item">
                    {t('seats', {
                      count: electionInfo?.seat_count,
                    })}
                  </div>
                  {roundNumber && (
                    <div className="electionteaser__meta-item">
                      {t('round', {
                        count: roundNumber,
                      })}
                    </div>
                  )}
                </div>
              </div>
              {isFinal && (
                <div className="electionteaser__bottom">
                  <ElectionTeaserResult
                    totalSeats={totalSeats}
                    electedSeats={claimedSeats}
                    isProportional={isProportional}
                  />
                </div>
              )}
            </div>
            {showInfo && (
              <ElectionTeaserInfo
                noSeats={noSeats}
                nextElectionDate={nextElectionDate}
                round={roundNumber}
              />
            )}
          </div>
        </Link>
      </div>
    </div>
  );
};

ElectionTeaser.defaultProps = {
  headingLevel: 3,
};

export default ElectionTeaser;
